import {
  DEFAULT_COMPANY_TIME_ZONE,
  zonedDateKey,
  zonedMidnight,
  zonedParts,
  type AnswerCard,
  type AttachmentCard,
  type CalendarCard,
  type EventCardItem,
  type MailCardItem,
  type MailListCard,
} from '@dbrain/shared';
import { formatBytes } from './format.js';

/**
 * Microsoft 365 answer cards on the screen (Phase 15e, D-197; MsTakvimYatay, MsMailListe, MsMailEk). The server
 * resolves the model's `oppy-kart` blocks into cards whose facts (senders, subjects, times, attendees, file names,
 * sizes) come from Graph; this only lays them out: where each card stands in its message, a day's strip (08:00–18:00,
 * wider when a meeting falls outside it) with its longest free stretch and totals, who is in a meeting ("Ayşe, Can
 * ve 4 kişi"), the mail list's groups and counts, an attachment's type, size and page chips, and every card as plain
 * text for Copy. Days and times are the card's own zone, the one the assistant answered in. Names, subjects and file
 * names are shown as Graph gave them: nothing here changes their case. DOM-free, shared with the native app (D-167).
 */
export type Translate = (key: string, options?: Record<string, unknown>) => string;

const K = 'microsoft365.chat';
const DAY_MS = 86_400_000;

// ---- where the cards stand in the message ---------------------------------------------------

export type AnswerPart = { kind: 'text'; text: string } | { kind: 'card'; card: AnswerCard; index: number };

/**
 * The message's text cut at each card's offset (the server puts it between blocks), the card after its cut.
 * Cards at the same place keep their order; an offset past the end puts the card last; blank pieces are left out.
 */
export function answerParts(text: string, cards: readonly AnswerCard[]): AnswerPart[] {
  const placed = cards
    .map((card, index) => ({ card, index, at: Number.isFinite(card.at) ? Math.min(Math.max(0, Math.floor(card.at)), text.length) : text.length }))
    .sort((a, b) => a.at - b.at || a.index - b.index);
  const parts: AnswerPart[] = [];
  let last = 0;
  for (const { card, index, at } of placed) {
    const piece = text.slice(last, at);
    if (piece.trim()) parts.push({ kind: 'text', text: piece });
    parts.push({ kind: 'card', card, index });
    last = at;
  }
  const rest = text.slice(last);
  if (rest.trim()) parts.push({ kind: 'text', text: rest });
  return parts;
}

// ---- time ------------------------------------------------------------------------------------

const zones = new Map<string, string>();

/** The card's zone when this runtime knows it, else the company default: a card never fails over its zone. */
export function cardZone(timeZone: string): string {
  let known = zones.get(timeZone);
  if (known === undefined) {
    try {
      known = Intl.DateTimeFormat('en-US', { timeZone }).resolvedOptions().timeZone ? timeZone : DEFAULT_COMPANY_TIME_ZONE;
    } catch {
      known = DEFAULT_COMPANY_TIME_ZONE;
    }
    zones.set(timeZone, known);
  }
  return known;
}

/** An instant from Graph-like text; a time without a zone is UTC (Graph answers in UTC), never the reader's clock. */
export function instantOf(iso: string): Date | null {
  let value = iso.trim();
  if (/T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(value)) value = `${value}Z`;
  // Graph writes seven fraction digits; three are enough everywhere
  value = value.replace(/(\.\d{3})\d+/, '$1');
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? new Date(ms) : null;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Days since 1970 of a YYYY-MM-DD date. */
function dayNumber(date: string): number {
  const [y = 1970, m = 1, d = 1] = date.split('-').map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / DAY_MS);
}

/** Midday UTC of a date: its weekday and month names, whatever the zone. */
const middayOf = (date: string) => new Date(dayNumber(date) * DAY_MS + 12 * 3_600_000);

/** The instant a date's day starts in the zone (its 00:00), or the start of the day `after` days later. */
function midnightOf(date: string, timeZone: string, after = 0): number {
  const [y = 1970, m = 1, d = 1] = date.split('-').map(Number);
  // Date.UTC carries a day past the month's end over: 31 October + 1 is 1 November
  return zonedMidnight(y, m, d + after, cardZone(timeZone)).getTime();
}

/** Minutes after the day's 00:00 on the zone's clock: negative for the day before, past 1440 for the day after. */
export function minutesInDay(iso: string, date: string, timeZone: string): number {
  const at = instantOf(iso);
  if (!at) return Number.NaN;
  const p = zonedParts(at, cardZone(timeZone));
  return (Math.round(Date.UTC(p.year, p.month - 1, p.day) / DAY_MS) - dayNumber(date)) * 1440 + p.hour * 60 + p.minute;
}

/** "09:30" on the zone's clock (24 hours, like the strip's hours); "" for an unreadable time. */
export function clockOf(iso: string, timeZone: string): string {
  const at = instantOf(iso);
  if (!at) return '';
  const p = zonedParts(at, cardZone(timeZone));
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

/** Minutes of a day as "12:00". */
export const clockOfMinutes = (minutes: number): string => `${pad(Math.floor(minutes / 60) % 24)}:${pad(((minutes % 60) + 60) % 60)}`;

/** The zone's date of an instant, YYYY-MM-DD ("" for an unreadable time). */
function dateOf(iso: string, timeZone: string): string {
  const at = instantOf(iso);
  return at ? zonedDateKey(at, cardZone(timeZone)) : '';
}

/** "2 saat", "1 saat 30 dk", "45 dk". */
export function durationText(minutes: number, t: Translate): string {
  const total = Math.max(0, Math.round(minutes));
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  if (hours > 0 && rest > 0) return t(`${K}.duration.hoursMinutes`, { hours, minutes: rest });
  if (hours > 0) return t(`${K}.duration.hours`, { count: hours });
  return t(`${K}.duration.minutes`, { count: rest });
}

/**
 * "Yarın · Cuma", "Bugün · Cumartesi", "3 Kasım · Salı" (with the year when it is not this year): a card's day
 * against today in the card's zone.
 */
export function dayLabel(date: string, timeZone: string, now: Date, locale: string, t: Translate): string {
  const today = zonedDateKey(now, cardZone(timeZone));
  const diff = dayNumber(date) - dayNumber(today);
  const midday = middayOf(date);
  const weekday = midday.toLocaleDateString(locale, { weekday: 'long', timeZone: 'UTC' });
  const day =
    diff === 0
      ? t(`${K}.days.today`)
      : diff === 1
        ? t(`${K}.days.tomorrow`)
        : diff === -1
          ? t(`${K}.days.yesterday`)
          : dateName(date, locale, today.slice(0, 4) !== date.slice(0, 4));
  return t(`${K}.days.label`, { day, weekday });
}

/** "31 Ekim" (with the year when asked). */
export function dateName(date: string, locale: string, withYear = false): string {
  return middayOf(date).toLocaleDateString(locale, { day: 'numeric', month: 'long', timeZone: 'UTC', ...(withYear ? { year: 'numeric' } : {}) });
}

/** The day badge: "EKİM" over "31"; a long month name only when it is short ("EKİM", "MAYIS"), else its short form ("AĞU"). */
export function dayBadge(date: string, locale: string): { month: string; day: string } {
  const midday = middayOf(date);
  const long = midday.toLocaleDateString(locale, { month: 'long', timeZone: 'UTC' });
  const month = long.length <= 5 ? long : midday.toLocaleDateString(locale, { month: 'short', timeZone: 'UTC' }).replace(/\.$/, '');
  return { month: month.toLocaleUpperCase(locale), day: String(midday.getUTCDate()) };
}

// ---- the calendar card (MsTakvimYatay) ---------------------------------------------------------

/**
 * Working hours on the card's clock: every strip shows at least 08:00–18:00, and free stretches are looked for only
 * in them (the worker's "Boş aralıklar (08:00–18:00)" says the same to the model).
 */
const WORK_FROM = 8 * 60;
const WORK_TO = 18 * 60;
const DAY_MINUTES = 24 * 60;
/** A meeting longer than this (a timed "all day", a block of several days) is drawn clipped and does not widen the strip. */
const LONG_MEETING = 12 * 60;
/** A strip longer than this writes every second hour. */
const EVERY_HOUR_UP_TO = 13 * 60;
/** On a narrow screen, a strip longer than this leaves every other number out. */
const NARROW_UP_TO = 9 * 60;

export interface StripHour {
  hour: number;
  /** where its line stands, in percent of the strip */
  at: number;
  /** its number is written over the strip */
  label: boolean;
  /** a narrow screen leaves its number out */
  minor: boolean;
}

/** The hours of a card's strips: one range for all its days, so that their hours line up. */
export interface StripRange {
  /** minutes after 00:00, on whole hours */
  from: number;
  to: number;
  /** every hour from `from` to `to`, each with a line */
  hours: StripHour[];
}

export interface StripBlock {
  ref: string;
  n: number;
  subject: string;
  online: boolean;
  /** where it starts and how wide it is, in percent of the strip */
  start: number;
  span: number;
  /** meetings at the same time share the strip's height: this one's lane of `lanes` */
  lane: number;
  lanes: number;
}

export interface FreeStretch {
  /** "12:00", "15:00" */
  from: string;
  to: string;
  minutes: number;
  /** in percent of the strip */
  start: number;
  span: number;
}

export interface CalendarDayView {
  date: string;
  /** every event of the day in the card's order (numbered 1…n across the card) */
  events: EventCardItem[];
  /** the hours the strip shows (the card's, when it has several days) */
  range: StripRange;
  blocks: StripBlock[];
  /** the day's listed meetings that were not cancelled, all-day ones too (the steps line and the model count them so) */
  meetings: number;
  /** time in meetings with a time that day, by their instants; meetings at the same time count once */
  minutes: number;
  /** the longest free stretch between 08:00 and 18:00 (the earliest of equal ones); null when there is none */
  gap: FreeStretch | null;
}

const percentOf = (minutes: number, whole: number) => Math.round((minutes / whole) * 10_000) / 100;

function merged(intervals: [number, number][]): [number, number][] {
  const out: [number, number][] = [];
  for (const [from, to] of [...intervals].sort((a, b) => a[0] - b[0])) {
    const last = out.at(-1);
    if (last && from <= last[1]) last[1] = Math.max(last[1], to);
    else out.push([from, to]);
  }
  return out;
}

/**
 * Meetings with a time that were not cancelled (the day's own, or `events` of another day seen from this one): their
 * instants, and where they stand on this day's clock in minutes (before it: negative, after it: past 1440). Whether
 * one is a meeting at all is its real length, never the clock: across a clock turned back, 01:30–01:30 is an hour,
 * drawn as one from its start.
 */
function timedOf(day: CalendarCard['days'][number], timeZone: string, events: readonly EventCardItem[] = day.events) {
  return events
    .filter((e) => !e.isCancelled && !e.isAllDay)
    .map((e) => {
      const start = instantOf(e.start)?.getTime() ?? Number.NaN;
      const end = instantOf(e.end)?.getTime() ?? Number.NaN;
      const from = minutesInDay(e.start, day.date, timeZone);
      const to = minutesInDay(e.end, day.date, timeZone);
      return { event: e, start, end, from, to: to > from ? to : from + (end - start) / 60_000 };
    })
    .filter((x) => x.end > x.start && Number.isFinite(x.from) && Number.isFinite(x.to));
}

/**
 * An event takes the person's time when the worker says so (`busy`: the free stretches the model is told count it
 * the same way). A card made before v0.15e.1 has no `busy`: a meeting with a time takes it, an all-day event does
 * not, as those cards were drawn.
 */
const takesTime = (event: EventCardItem): boolean => !event.isCancelled && (event.busy ?? !event.isAllDay);

/**
 * The strip of a card's days: 08:00–18:00, widened to whole hours until it holds every meeting with a time on those
 * days. A meeting is drawn on the day it is listed under (its start), so one that goes past midnight runs to 24:00
 * there. Cancelled and all-day events are only listed, and a meeting longer than 12 hours is drawn clipped: none of
 * them widens the strip. Numbers every hour, every second hour on a strip longer than 13 hours.
 */
export function stripRange(days: readonly CalendarCard['days'][number][], timeZone: string): StripRange {
  let from = WORK_FROM;
  let to = WORK_TO;
  for (const day of days) {
    for (const x of timedOf(day, timeZone)) {
      if (x.to - x.from > LONG_MEETING) continue;
      const start = Math.max(0, x.from);
      const end = Math.min(DAY_MINUTES, x.to);
      if (end <= start) continue;
      from = Math.min(from, Math.floor(start / 60) * 60);
      to = Math.max(to, Math.ceil(end / 60) * 60);
    }
  }
  const every = to - from > EVERY_HOUR_UP_TO ? 2 : 1;
  const hours: StripHour[] = [];
  for (let minute = from; minute <= to; minute += 60) {
    const hour = minute / 60;
    const label = hour % every === 0;
    // a phone keeps the numbers of the even hours (of every fourth on a long strip), wherever the strip starts
    hours.push({ hour, at: percentOf(minute - from, to - from), label, minor: label && to - from > NARROW_UP_TO && (hour / every) % 2 === 1 });
  }
  return { from, to, hours };
}

/**
 * A day of the calendar card: the strip's blocks (overlaps in lanes), the longest free stretch and the totals. A card
 * of several days passes its own `range`, so that every day's strip shows the same hours, and its `days`: a meeting
 * listed under an earlier day (where it starts) that runs into this one, or an all-day event of several days, keeps
 * this day's time too, as in the free stretches the model is told. The day's strip, list and totals stay its own.
 */
export function calendarDay(
  day: CalendarCard['days'][number],
  timeZone: string,
  range: StripRange = stripRange([day], timeZone),
  days: readonly CalendarCard['days'][number][] = [day],
): CalendarDayView {
  const events = [...day.events].sort((a, b) => a.n - b.n);
  const timed = timedOf(day, timeZone);
  // real minutes between the day's 00:00 and the next: a night the clock turns is an hour shorter or longer
  const dayStart = midnightOf(day.date, timeZone);
  const dayEnd = midnightOf(day.date, timeZone, 1);
  const minutes = merged(timed.map((x) => [Math.max(dayStart, x.start), Math.min(dayEnd, x.end)] as [number, number]).filter(([a, b]) => b > a)).reduce((sum, [a, b]) => sum + b - a, 0) / 60_000;
  const percent = (value: number) => percentOf(value, range.to - range.from);
  const onStrip = timed
    .map((x) => ({ ...x, from: Math.max(x.from, range.from), to: Math.min(x.to, range.to) }))
    .filter((x) => x.to > x.from)
    .sort((a, b) => a.from - b.from || b.to - a.to || a.event.n - b.event.n);
  // meetings that overlap form a cluster; each takes the first lane free at its start
  const blocks: StripBlock[] = [];
  let cluster: StripBlock[] = [];
  let laneEnds: number[] = [];
  let clusterEnd = -Infinity;
  const close = () => {
    for (const block of cluster) block.lanes = laneEnds.length;
    cluster = [];
    laneEnds = [];
  };
  for (const x of onStrip) {
    if (x.from >= clusterEnd) close();
    let lane = laneEnds.findIndex((end) => end <= x.from);
    if (lane < 0) lane = laneEnds.push(x.to) - 1;
    else laneEnds[lane] = x.to;
    const block: StripBlock = {
      ref: x.event.ref,
      n: x.event.n,
      subject: x.event.subject,
      online: x.event.online,
      start: percent(x.from - range.from),
      span: percent(x.to - x.from),
      lane,
      lanes: 1,
    };
    cluster.push(block);
    blocks.push(block);
    clusterEnd = Math.max(clusterEnd, x.to);
  }
  close();
  // the free stretches between the busy ones, in working hours only however wide the strip; the first of the longest
  // wins. Busy is what takes the person's time: this day's meetings and the part of an earlier day's that runs into it;
  // an all-day event that takes it, listed here or on an earlier day and not over by this one, fills the working day
  const earlier = days.filter((other) => other.date < day.date).flatMap((other) => other.events);
  const carried = timedOf(day, timeZone, earlier).filter((x) => x.end > dayStart);
  const allDay = [...day.events, ...earlier.filter((e) => (instantOf(e.end)?.getTime() ?? Number.NaN) > dayStart)].filter((e) => e.isAllDay && takesTime(e));
  const atWork = (minute: number) => Math.min(Math.max(minute, WORK_FROM), WORK_TO);
  const busy = [...timed, ...carried].filter((x) => takesTime(x.event)).map((x) => [atWork(x.from), atWork(x.to)] as [number, number]);
  if (allDay.length > 0) busy.push([WORK_FROM, WORK_TO]);
  let gap: { from: number; to: number } | null = null;
  let cursor = WORK_FROM;
  for (const [from, to] of [...merged(busy), [WORK_TO, WORK_TO] as [number, number]]) {
    if (from - cursor > (gap ? gap.to - gap.from : 0)) gap = { from: cursor, to: from };
    cursor = Math.max(cursor, to);
  }
  return {
    date: day.date,
    events,
    range,
    blocks,
    meetings: events.filter((e) => !e.isCancelled).length,
    minutes,
    gap: gap
      ? { from: clockOfMinutes(gap.from), to: clockOfMinutes(gap.to), minutes: gap.to - gap.from, start: percent(gap.from - range.from), span: percent(gap.to - gap.from) }
      : null,
  };
}

/** The day's chips: "3 toplantı", "toplam 2 saat", "en uzun boşluk 12:00–15:00" (a day without meetings: "Toplantı yok"). */
export function calendarChips(day: CalendarDayView, t: Translate): string[] {
  if (day.meetings === 0) return [t(`${K}.calendar.noMeetings`)];
  const chips = [t(`${K}.calendar.meetings`, { count: day.meetings })];
  if (day.minutes > 0) chips.push(t(`${K}.calendar.total`, { duration: durationText(day.minutes, t) }));
  if (day.gap) chips.push(t(`${K}.calendar.longestGap`, { from: day.gap.from, to: day.gap.to }));
  return chips;
}

/** "09:30–10:00", or "Tüm gün". */
export function eventTime(event: Pick<EventCardItem, 'start' | 'end' | 'isAllDay'>, timeZone: string, t: Translate): string {
  if (event.isAllDay) return t(`${K}.calendar.allDay`);
  return `${clockOf(event.start, timeZone)}–${clockOf(event.end, timeZone)}`;
}

/**
 * Where a meeting is: "Teams" only when Graph says the meeting is on Teams (`teamsForBusiness`), any other online
 * meeting "Çevrim içi" (Skype, another service, or no provider known), else its room; nothing when none is known.
 */
export function eventPlace(event: Pick<EventCardItem, 'online' | 'location' | 'provider'>, t: Translate): { kind: 'online' | 'room'; text: string } | null {
  if (event.online) return { kind: 'online', text: t(event.provider?.trim().toLowerCase() === 'teamsforbusiness' ? `${K}.calendar.teams` : `${K}.calendar.online`) };
  const room = event.location.trim();
  return room ? { kind: 'room', text: room } : null;
}

const lower = (value: string) => value.trim().toLowerCase();

/** A person's name as written, or the start of their address. */
function personName(p: { name: string; address: string }): string {
  const name = p.name.trim();
  if (name && !name.includes('@')) return name;
  return (name || p.address).split('@')[0]?.trim() || p.address;
}

const firstName = (p: { name: string; address: string }) => personName(p).split(/\s+/)[0] ?? personName(p);

/** The people of a meeting other than you, organizer first; `self` adds your own addresses (an invitee's row). */
function othersOf(event: Pick<EventCardItem, 'organizer' | 'attendees'>, self: readonly string[]) {
  const mine = new Set(self.map(lower).filter(Boolean));
  if (event.organizer.isYou && event.organizer.address) mine.add(lower(event.organizer.address));
  const seen = new Set<string>();
  const out: { name: string; address: string }[] = [];
  for (const p of [...(event.organizer.isYou ? [] : [event.organizer]), ...event.attendees]) {
    const key = lower(p.address) || lower(p.name);
    if (!key || seen.has(key) || mine.has(lower(p.address))) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

/**
 * Who is in a meeting, as the mockup says it: "Ayşe Demir" (one other person), "Elif Şahin ve siz" (one other in
 * a meeting you organized), "Ayşe ve Can", "Ayşe, Can ve 4 kişi"; "Yalnız siz" for a meeting of your own.
 */
export function attendeeSummary(event: Pick<EventCardItem, 'organizer' | 'attendees'>, t: Translate, self: readonly string[] = []): string {
  const others = othersOf(event, self);
  const you = event.organizer.isYou;
  const [a, b] = others;
  if (!a) return you ? t(`${K}.attendees.onlyYou`) : '';
  if (!b) return t(you ? `${K}.attendees.oneAndYou` : `${K}.attendees.one`, { name: personName(a) });
  if (others.length === 2) return t(you ? `${K}.attendees.twoAndYou` : `${K}.attendees.two`, { first: firstName(a), second: firstName(b) });
  return t(`${K}.attendees.many`, { first: firstName(a), second: firstName(b), count: others.length - 2 });
}

// ---- the mail list card (MsMailListe) ------------------------------------------------------------

export type MailGroupKind = 'reply' | 'info';

export interface MailListView {
  /** the model's title, else the source ("Outlook · Gelen kutusu · 30 Ekim") */
  title: string;
  /** the source under a title the model gave: "Outlook · Gelen kutusu · 30 Ekim" */
  subtitle: string | null;
  /** the mails the card lists: its groups and the rest behind "Göster" */
  listed: number;
  /** of them, waiting for a reply */
  replies: number;
  /** "Yanıt bekleyenler" first, then "Bilgi için"; empty groups left out */
  groups: { kind: MailGroupKind; items: MailCardItem[] }[];
  /**
   * "N mail daha": the listed mails after the groups (`count` of them, shown on "Göster"); `more` are the ones Graph
   * counted but did not list, which the card can only name
   */
  rest: { count: number; summary: string | null; items: MailCardItem[]; more: number };
  /** every mail of the card came on the one day its source names ("Gelen kutusu · 30 Ekim"): rows say only the time */
  sameDay: boolean;
}

const GROUP_ORDER: MailGroupKind[] = ['reply', 'info'];

export function mailListView(card: MailListCard, t: Translate): MailListView {
  const seen = new Set<string>();
  const fresh = (items: MailCardItem[]) =>
    items.filter((item) => {
      if (seen.has(item.ref)) return false;
      seen.add(item.ref);
      return true;
    });
  const groups = GROUP_ORDER.map((kind) => ({ kind, items: fresh(card.groups.filter((g) => g.kind === kind).flatMap((g) => g.items)) })).filter((g) => g.items.length > 0);
  const restItems = fresh(card.rest.items);
  const all = [...groups.flatMap((g) => g.items), ...restItems];
  const days = new Set(all.map((item) => dateOf(item.receivedAt, card.timeZone)));
  // the product's name beside Microsoft's icon (D-201)
  const source = t(`${K}.mail.source`, { label: card.source.label });
  const title = card.title?.trim();
  return {
    title: title || source,
    subtitle: title ? source : null,
    listed: all.length,
    replies: groups.find((g) => g.kind === 'reply')?.items.length ?? 0,
    groups,
    rest: { count: restItems.length, summary: card.rest.summary?.trim() || null, items: restItems, more: card.rest.more },
    // mails of one day found without a day ("Gelen kutusu") or in a week keep their date: nothing else on the card names it
    sameDay: days.size === 1 && card.source.day !== undefined && days.has(card.source.day),
  };
}

/** The header's chips, about the mails listed: "10 mail", "2 yanıt bekliyor". */
export function mailChips(view: MailListView, t: Translate): string[] {
  const chips = [t(`${K}.mail.count`, { count: view.listed })];
  if (view.replies > 0) chips.push(t(`${K}.mail.replies`, { count: view.replies }));
  return chips;
}

/** "20 mail daha: bültenler, …", the listed rest. */
export function restText(view: MailListView, t: Translate): string {
  return view.rest.summary ? t(`${K}.mail.restWith`, { count: view.rest.count, summary: view.rest.summary }) : t(`${K}.mail.rest`, { count: view.rest.count });
}

/** "Listelenmeyen 51 mail daha var.": the mails Graph counted but did not list; null when it listed them all. */
export function notListedText(view: MailListView, t: Translate): string | null {
  return view.rest.more > 0 ? t(`${K}.mail.notListed`, { count: view.rest.more }) : null;
}

/** When a mail came: "09:12" on a card of one day, else "30 Eki 09:12" (with the year when it is not this year). */
export function mailTime(receivedAt: string, sameDay: boolean, timeZone: string, locale: string, now: Date): string {
  const at = instantOf(receivedAt);
  if (!at) return '';
  const zone = cardZone(timeZone);
  const time = clockOf(receivedAt, zone);
  if (sameDay) return time;
  const sameYear = zonedParts(at, zone).year === zonedParts(now, zone).year;
  return `${at.toLocaleDateString(locale, { day: 'numeric', month: 'short', timeZone: zone, ...(sameYear ? {} : { year: 'numeric' }) })} ${time}`;
}

/** The sender as the row shows it. */
export const senderName = (from: { name: string; address: string }): string => personName(from);

/** The row's hover text: the sender's address, which a display name cannot fake ("Ayşe Demir" from anyone); none when unknown. */
export const senderAddress = (from: { name: string; address: string }): string | undefined => from.address.trim() || undefined;

// ---- the attachment card (MsMailEk) ------------------------------------------------------------

export type FileKind = 'pdf' | 'word' | 'excel' | 'powerpoint' | 'csv' | 'text' | 'image' | 'archive' | 'other';

const KIND_BY_EXTENSION: Record<string, FileKind> = {
  pdf: 'pdf',
  doc: 'word',
  docx: 'word',
  docm: 'word',
  odt: 'word',
  rtf: 'word',
  xls: 'excel',
  xlsx: 'excel',
  xlsm: 'excel',
  ods: 'excel',
  ppt: 'powerpoint',
  pptx: 'powerpoint',
  pptm: 'powerpoint',
  odp: 'powerpoint',
  csv: 'csv',
  txt: 'text',
  md: 'text',
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  gif: 'image',
  webp: 'image',
  heic: 'image',
  tif: 'image',
  tiff: 'image',
  zip: 'archive',
  rar: 'archive',
  '7z': 'archive',
};

/** What a file is, by its type and else its name: the card's badge and "PDF · 12 sayfa". */
export function fileKind(name: string, contentType: string): FileKind {
  const type = lower(contentType);
  if (type === 'application/pdf') return 'pdf';
  if (type.includes('wordprocessingml') || type === 'application/msword' || type.includes('opendocument.text')) return 'word';
  if (type.includes('spreadsheetml') || type === 'application/vnd.ms-excel' || type.includes('opendocument.spreadsheet')) return 'excel';
  if (type.includes('presentationml') || type === 'application/vnd.ms-powerpoint' || type.includes('opendocument.presentation')) return 'powerpoint';
  if (type === 'text/csv') return 'csv';
  if (type.startsWith('image/')) return 'image';
  const extension = lower(name).split('.').pop() ?? '';
  return KIND_BY_EXTENSION[extension] ?? (type.startsWith('text/') ? 'text' : 'other');
}

/** The short badge on a file ("PDF", "XLS"); none for a file of no known kind. */
export const FILE_BADGE: Record<FileKind, string> = { pdf: 'PDF', word: 'DOC', excel: 'XLS', powerpoint: 'PPT', csv: 'CSV', text: 'TXT', image: 'IMG', archive: 'ZIP', other: '' };

export const fileTypeLabel = (kind: FileKind, t: Translate): string => t(`${K}.fileTypes.${kind}`);

/** How long a read attachment is: pages (PDF, Word), slides or sheets; unknown before it was read. */
export function attachmentExtent(a: AttachmentCard['attachment']): { kind: 'pages' | 'slides' | 'sheets'; count: number } | null {
  if (a.slides) return { kind: 'slides', count: a.slides };
  if (a.sheets && a.sheets.length > 0) return { kind: 'sheets', count: a.sheets.length };
  if (a.pages) return { kind: 'pages', count: a.pages };
  return null;
}

/** "PDF · 12 sayfa · 2,4 MB" */
export function attachmentMeta(a: AttachmentCard['attachment'], locale: string, t: Translate): string {
  const extent = attachmentExtent(a);
  return [fileTypeLabel(fileKind(a.name, a.contentType), t), extent ? t(`${K}.attachment.extent.${extent.kind}`, { count: extent.count }) : null, formatBytes(a.size, locale)]
    .filter(Boolean)
    .join(' · ');
}

/** Where a summary point is in the attachment: "s. 2", "s. 11–12" (slides: "slayt 3"); nothing when unknown. */
export function pointPlace(pages: { from: number; to: number } | null, t: Translate, slides = false): string | null {
  if (!pages) return null;
  const unit = slides ? 'slide' : 'page';
  return pages.to > pages.from ? t(`${K}.attachment.${unit}s`, { from: pages.from, to: pages.to }) : t(`${K}.attachment.${unit}`, { from: pages.from });
}

const BACK_VOWELS: Record<string, string> = { a: 'ı', ı: 'ı', â: 'ı', e: 'i', i: 'i', î: 'i', o: 'u', u: 'u', û: 'u', ö: 'ü', ü: 'ü' };

/**
 * The Turkish genitive ending of a name, with its apostrophe, by the last vowel and whether the name ends in one:
 * Can Aydın'ın, Ayşe Demir'in, Selin Kaya'nın, Mert Öz'ün.
 */
export function trGenitiveSuffix(name: string): string {
  const letters = name.toLocaleLowerCase('tr').replace(/[^a-zçğıöşüâîû]/g, '');
  const vowels = [...letters].filter((ch) => ch in BACK_VOWELS);
  // letters read out one by one end in a vowel: BT'nin
  if (vowels.length === 0) return "'nin";
  const vowel = BACK_VOWELS[vowels.at(-1) ?? 'e'] ?? 'i';
  const last = letters.at(-1) ?? '';
  return last in BACK_VOWELS ? `'n${vowel}n` : `'${vowel}n`;
}

/** The person a mail is from, without a company after " · " ("Elif Yılmaz · Akasya Lojistik" is Elif Yılmaz's). */
const possessor = (from: { name: string; address: string }) => senderName(from).split(' · ')[0]?.trim() || senderName(from);

/**
 * "Outlook · Can Aydın'ın maili · Q3 bölge raporu · 30 Eki 11:05": the product's name first, beside Microsoft's icon
 * (D-201); a mail without a sender (a draft) is named by its subject and time only.
 */
export function attachmentSource(card: Pick<AttachmentCard, 'message' | 'timeZone'>, locale: string, t: Translate, now: Date): string {
  const name = possessor(card.message.from);
  const label = [name ? t(`${K}.attachment.fromMail`, { name, suffix: trGenitiveSuffix(name) }) : null, card.message.subject.trim(), mailTime(card.message.receivedAt, false, card.timeZone, locale, now)]
    .filter(Boolean)
    .join(' · ');
  return t(`${K}.attachment.source`, { label });
}

// ---- plain text for Copy -------------------------------------------------------------------------

export interface CardTextContext {
  t: Translate;
  locale: string;
  now: Date;
  /** the reader's own addresses (their Microsoft account), left out of a meeting's people */
  self?: readonly string[];
}

function calendarText(card: CalendarCard, ctx: CardTextContext): string {
  const { t, locale, now } = ctx;
  const zone = cardZone(card.timeZone);
  const today = dayNumber(zonedDateKey(now, zone));
  return card.days
    .map((day) => {
      const view = calendarDay(day, card.timeZone, undefined, card.days);
      const relative = Math.abs(dayNumber(day.date) - today) <= 1;
      const head = `${dayLabel(day.date, card.timeZone, now, locale, t)}${relative ? ` (${dateName(day.date, locale)})` : ''} · ${t(`${K}.calendar.source`)}`;
      const rows = view.events.map((e) => {
        const place = eventPlace(e, t);
        const people = attendeeSummary(e, t, ctx.self);
        return `${e.n}. ${[eventTime(e, card.timeZone, t), e.subject, place?.text, people, e.isCancelled ? t(`${K}.calendar.cancelled`) : null].filter(Boolean).join(' · ')}`;
      });
      return [head, calendarChips(view, t).join(' · '), ...rows].join('\n');
    })
    .join('\n\n');
}

function mailRowText(item: MailCardItem, view: MailListView, card: MailListCard, ctx: CardTextContext, waiting: boolean): string {
  const { t } = ctx;
  const head = [senderName(item.from), mailTime(item.receivedAt, view.sameDay, card.timeZone, ctx.locale, ctx.now), item.subject].filter(Boolean).join(' · ');
  const tags = [waiting ? t(`${K}.mail.waiting`) : null, item.attachments.length > 0 ? t(`${K}.mail.attachments`, { names: item.attachments.map((a) => a.name).join(', ') }) : null].filter(Boolean);
  return `- ${head}${item.summary ? ` — ${item.summary}` : ''}${tags.length > 0 ? ` (${tags.join('; ')})` : ''}`;
}

function mailText(card: MailListCard, ctx: CardTextContext): string {
  const { t } = ctx;
  const view = mailListView(card, t);
  const lines = [[view.title, view.subtitle].filter(Boolean).join(' · '), mailChips(view, t).join(' · ')];
  for (const group of view.groups) {
    lines.push('', t(`${K}.mail.groups.${group.kind}`), ...group.items.map((item) => mailRowText(item, view, card, ctx, group.kind === 'reply')));
  }
  // a plain list (no groups) shows its rows on the card, so Copy writes them too
  const plain = view.groups.length === 0;
  if (plain && view.rest.items.length > 0) lines.push('', ...view.rest.items.map((item) => mailRowText(item, view, card, ctx, false)));
  const notListed = notListedText(view, t);
  if ((view.rest.count > 0 && !plain) || notListed) lines.push('');
  if (view.rest.count > 0 && !plain) lines.push(restText(view, t));
  if (notListed) lines.push(notListed);
  return lines.join('\n');
}

function attachmentText(card: AttachmentCard, ctx: CardTextContext): string {
  const { t, locale } = ctx;
  const slides = attachmentExtent(card.attachment)?.kind === 'slides';
  const lines = [`${card.attachment.name} · ${attachmentMeta(card.attachment, locale, t)}`, attachmentSource(card, locale, t, ctx.now)];
  if (card.points.length > 0) {
    lines.push('', t(`${K}.attachment.summary`));
    for (const point of card.points) {
      const place = pointPlace(point.pages, t, slides);
      lines.push(`- ${point.text.trim()}${place ? ` (${place})` : ''}`);
    }
  }
  lines.push('', t(`${K}.attachment.onlyHere`));
  if (card.siblings.length > 0) {
    lines.push(`${t(`${K}.attachment.siblings`, { count: card.siblings.length })} ${card.siblings.map((s) => `${s.name} · ${formatBytes(s.size, locale)}`).join(', ')}`);
  }
  return lines.join('\n');
}

/** A card as plain text, the way Copy puts it after the text around it. */
export function cardPlainText(card: AnswerCard, ctx: CardTextContext): string {
  switch (card.kind) {
    case 'calendar':
      return calendarText(card, ctx);
    case 'mail_list':
      return mailText(card, ctx);
    case 'attachment':
      return attachmentText(card, ctx);
    default:
      return '';
  }
}

/** An answer for Copy: its text with each card's plain text where the card stands. */
export function answerPlainText(text: string, cards: readonly AnswerCard[], ctx: CardTextContext): string {
  if (cards.length === 0) return text;
  return answerParts(text, cards)
    .map((part) => (part.kind === 'text' ? part.text.trim() : cardPlainText(part.card, ctx)))
    .filter(Boolean)
    .join('\n\n');
}
